import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HelpGuide, HelpGuideSearchResult, HelpGuideSummary } from "../../common/help-model";
import { RepositoryService } from "../../linq-utils/repository.service";

const COMPONENT = "HelpGuidesService"

export const HELP_URL = 'help'
export const HELP_GUIDE_URL = (slug:string) => `${HELP_URL}/${slug}`

// the organisation a page is in, from its address: its own pages and the introduction to its models, but not starting one
export const organisationInUrl = (url:string|undefined) => url?.match(/^\/(?:introduction\/)?organisations\/(?!get-started\b)([^/?#]+)/)?.[1]

// the help library, or a guide in it, from the page at the address given: inside its organisation, if it is in one
export const helpUrlFrom = (currentUrl:string, slug?:string) => {
    const organisation = organisationInUrl(currentUrl)
    const url = slug ? HELP_GUIDE_URL(slug) : HELP_URL
    return organisation ? `organisations/${organisation}/${url}` : url
}

// the words of a search, lower case
const words = (text:string):string[] => text.toLowerCase().match(/[a-z0-9]+/g) || []

// the guides whose title, summary, category or keywords have every word searched for, or start with it, so a match
// shows while the word is still being typed
export const matchGuides = (guides:HelpGuideSummary[], query:string):HelpGuideSummary[] => {
    const wanted = words(query)
    if (!wanted.length) return guides
    const inTitle = (g:HelpGuideSummary) => wanted.every(w => words(g.title).some(h => h.startsWith(w)))
    return guides.filter(g => {
        const has = words([g.title, g.summary, g.category, ...(g.keywords || [])].join(' '))
        return wanted.every(w => has.some(h => h.startsWith(w)))
    // those with it in their title first
    }).sort((a, b) => Number(inTitle(b)) - Number(inTitle(a)))
}

// the help guides, held on the server: the list is fetched once and each guide's page when it's first opened. Errors
// aren't shown here, as search and the menu fetch the list on every screen: the help library says when it can't load them
@Injectable({providedIn: 'root'})
export class HelpGuidesService {
    private list?:Promise<HelpGuideSummary[]>
    private pages = new Map<string, Promise<HelpGuide>>()

    constructor(private repoService:RepositoryService, private router:Router) {}

    guides():Promise<HelpGuideSummary[]> {
        if (!this.list) {
            this.list = this.repoService.callSingleAPI(this.repoService.getData<HelpGuideSummary[]>('api/help/guides'), COMPONENT, "get guides", undefined, undefined, undefined, undefined, false)
            // a failed fetch is tried again next time
            this.list.catch(() => this.list = undefined)
        }
        return this.list
    }

    guide(slug:string):Promise<HelpGuide> {
        let page = this.pages.get(slug)
        if (!page) {
            page = this.repoService.callSingleAPI(this.repoService.getData<HelpGuide>(`api/help/guides/${encodeURIComponent(slug)}`), COMPONENT, "get guide", undefined, undefined, undefined, undefined, false)
            this.pages.set(slug, page)
            page.catch(() => this.pages.delete(slug))
        }
        return page
    }

    // the guides that mention what's searched for anywhere in them, best first
    search(query:string):Promise<HelpGuideSearchResult[]> {
        return this.repoService.callSingleAPI(this.repoService.getData<HelpGuideSearchResult[]>(`api/help/guides/search?q=${encodeURIComponent(query)}`), COMPONENT, "search guides", undefined, undefined, undefined, undefined, false)
    }

    // the help library, at a guide if one is given, in the organisation being worked in so its menu stays
    open(slug?:string) {
        return this.router.navigateByUrl(helpUrlFrom(this.router.url, slug))
    }
}
